import _ from 'lodash';
import { pendingRequestsTypesForms } from './forms.types';

import { pendingRequestsTypes } from 'constants/requests.constants';
import { apiFunctionsEnum } from 'constants/api.constants';
import { newUserProps } from 'constants/new_accounts.constants';
import { SupportStatus } from 'constants/pending-requests.constants';
import { BalanceStatus, depositType } from 'constants/balance.constants';
import { UserPermissionType, generalStatusEnum } from 'constants/global.constants';
import { format, isValid, startOfDay } from 'date-fns';
import { validationTypes, fieldMode } from 'components/FormCreatorComponent/types';
import { getPaymentCategoryDisplayById } from 'services/payment-categories.service';

export function getFormsValuesByType(record: Record<string, any>, type = '') {
	const forms = _.get(pendingRequestsTypesForms, type);
	const obj = {};
	if ([pendingRequestsTypes.BANK_ACCOUNTS, pendingRequestsTypes.BALANCES].includes(type as pendingRequestsTypes)) {
		return {
			...record,
			'createdBy.fullName':
				_.get(record, 'createdBy.firstName', '') + ' ' + _.get(record, 'createdBy.lastName', ''),
		};
	}
	if ([pendingRequestsTypes.CONTACTS].includes(type as pendingRequestsTypes)) {
		const dateOfBirth =
			!_.isNull(_.get(record, 'dateOfBirth')) && isValid(new Date(_.get(record, 'dateOfBirth')))
				? new Date(_.get(record, 'dateOfBirth'))
				: undefined;

		return {
			...record,
			day: dateOfBirth ? '' + (dateOfBirth?.getDate() - 1) : undefined,
			month: '' + dateOfBirth?.getMonth(),
			year: '' + dateOfBirth?.getFullYear(),
		};
	}
	if (
		[
			pendingRequestsTypes.PAYMENTS,
			pendingRequestsTypes.BENEFICIARIES,
			pendingRequestsTypes.WITHDRAWAL_SUPPORT,
		].includes(type as pendingRequestsTypes)
	) {
		const flattenedFiles: Record<string, any> = {};
		if (
			(type === pendingRequestsTypes.BENEFICIARIES || type === pendingRequestsTypes.PAYMENTS) &&
			record.files &&
			Array.isArray(record.files)
		) {
			record.files.forEach((fileWrapper: any, index: number) => {
				if (fileWrapper.file) {
					flattenedFiles[`files[${index}]`] = fileWrapper.file;
				}
			});
		}

		// Extract WC audit data from structured object
		const hasWcIndications = type === pendingRequestsTypes.BENEFICIARIES && record.wcAudit?.hasIndications ? 'true' : 'false';
		const wcCaseId = type === pendingRequestsTypes.BENEFICIARIES ? record.wcAudit?.caseId || '' : '';

		return {
			...record,
			...flattenedFiles,
			...getPaymentTypes(record),
			// Add computed payment category display
			paymentCategoryDisplay: getPaymentCategoryDisplayById(record.paymentCategoryId),
			currency: record?.associatedCurrency?.id ? record?.associatedCurrency?.id : record.currency,
			amount: record.amount === 0 ? '' : record.amount,
			'createdBy.fullName':
				_.get(record, 'createdBy.firstName', '') + ' ' + _.get(record, 'createdBy.lastName', ''),
			'approvedByContactId.fullName':
				_.has(record, 'approvedByContactId.firstName') || _.has(record, 'approvedByContactId.lastName')
					? _.get(record, 'approvedByContactId.firstName', '') +
					  ' ' +
					  _.get(record, 'approvedByContactId.lastName', '')
					: '',
			'signedByContact.fullName':
				_.get(record, 'signedByContact.firstName', '') + ' ' + _.get(record, 'signedByContact.lastName', ''),
			'bankAccountCountry.isRisky': String(_.get(record, 'bankAccountCountry.isRisky')),
			'bankAccountCountry.isTaxShelter': String(_.get(record, 'bankAccountCountry.isTaxShelter')),
			'country.isTaxShelter': String(_.get(record, 'country.isTaxShelter')),
			countryAtRiskDuringCreation: _.isNull(record.countryAtRiskDuringCreation)
				? String(record?.country?.isRisky)
				: String(record?.countryAtRiskDuringCreation),
			hasWcIndications,
			wcCaseId,
		};
	}
	forms.forEach((form) => {
		form.fieldsGroups.forEach((group) => {
			group.fields.forEach((field) => {
				const value = _.get(record, field.props.name, '');
				_.assign(obj, { [field.props.name]: value });
			});
		});
	});
	return obj;
}

export function getPaymentTypes(beneficiary: any) {
	if (_.has(beneficiary, 'isLocalPaymentAllowed')) {
		const payments = [];
		if (beneficiary?.isLocalPaymentAllowed) {
			payments.push('Regular');
		}
		if (beneficiary?.isPriorityPaymentAllowed) {
			payments.push('Priority');
		}
		return {
			paymentType: payments.join('/'),
		};
	}
	return {};
}

export function getFeaturePayload(type: string, id: string) {
	let props: { name: string; data: Record<string, any> } = {
		name: '',
		data: {},
	};

	if (type === pendingRequestsTypes.BENEFICIARIES) {
		props = {
			name: apiFunctionsEnum.GET_BENEFICIARY as string,
			data: {
				beneficiaryId: id,
			},
		};
	}
	if (
		[pendingRequestsTypes.BALANCES, pendingRequestsTypes.BALANCES_BO_REQUESTS].includes(
			type as pendingRequestsTypes
		)
	) {
		props = {
			name: apiFunctionsEnum.GET_BALANCE as string,
			data: {
				id: id,
			},
		};
	}
	if (type === pendingRequestsTypes.PAYMENTS) {
		props = {
			name: apiFunctionsEnum.GET_PAYMENT as string,
			data: {
				id: id,
			},
		};
	}
	if (type === pendingRequestsTypes.CONTACTS) {
		props = {
			name: apiFunctionsEnum.GET_ACCOUNT as string,
			data: {
				id: id,
			},
		};
	}
	if (type === pendingRequestsTypes.BANK_ACCOUNTS) {
		props = {
			name: apiFunctionsEnum.GET_BANK_ACCOUNT as string,
			data: {
				id: id,
			},
		};
	}
	if ([pendingRequestsTypes.GENERAL_SUPPORT, pendingRequestsTypes.WITHDRAWAL_SUPPORT].includes(type)) {
		props = {
			name: apiFunctionsEnum.GET_SUPPORT_REQUEST as string,
			data: {
				id: id,
			},
		};
	}

	return props;
}

export function getCompanyFeatureApprovePayload(
	action: string,
	type: string,
	id: string,
	BoNotes: string | null,
	item?: Record<string, any> | any
) {
	let props: { name: string; data: Record<string, any> } = {
		name: '',
		data: {},
	};
	const boNotes = !_.isNil(BoNotes) ? { boNotes: BoNotes } : {};

	if (type === pendingRequestsTypes.BALANCES) {
		if (action === 'approve') {
			const { id, altshulerAccountId, amount, bankAccountId, file, ccHouseAccountTransferReference } = item;
			props.name = apiFunctionsEnum.APPROVE_BALANCE as string;
			props.data = {
				id,
				bankAccountId,
				altshulerAccountId,
				amount: Number(amount),
			};
			if (!item?.file?.id) {
				props.data = { ...props.data, file };
			}
			if (ccHouseAccountTransferReference) {
				props.data = { ...props.data, ccHouseAccountTransferReference };
			}
		} else {
			props = {
				name: apiFunctionsEnum.BO_SIGNER_DECLINE_BALANCE as string,
				data: {
					id,
					reason: BoNotes,
				},
			};
		}
	}
	if (type === pendingRequestsTypes.BENEFICIARIES) {
		props = {
			name: apiFunctionsEnum.APPROVE_BENEFICIARY as string,
			data: {
				beneficiaryId: id,
				status:
					action === 'approve'
						? 'Approved'
						: action === 'DeclinedForRepair'
						? 'DeclinedForRepair'
						: 'Declined',
				...boNotes,
			},
		};
	}
	if (type === pendingRequestsTypes.PAYMENTS) {
		props = {
			name: apiFunctionsEnum.APPROVE_PAYMENT as string,
			data: {
				paymentId: id,
				status: action === 'approve' ? 'Approved' : 'Rejected',
				...boNotes,
			},
		};
	}
	if (type === pendingRequestsTypes.CONTACTS) {
		const contactStatus = action === 'approve' ? {} : { status: 'Rejected' };
		const dateOfBirth = new Date(item?.year, item?.month, _.toNumber(item?.day) + 1, 12);
		props = {
			name: apiFunctionsEnum.APPROVE_ACCOUNT as string,
			data: {
				contact: _.pick(
					{
						...item,
						role: item?.occupation || ' ',
						firstNameEnglish: item?.firstName,
						lastNameEnglish: item?.lastName,
						dateOfBirth:
							dateOfBirth instanceof Date && !isNaN(dateOfBirth.valueOf()) ? dateOfBirth : 'test10char',
						occupation: item?.occupation || ' ',
						phoneNumber: item?.phone,
						emailAddress: item?.email,
						permission: item?.permission || UserPermissionType.None,
						maxForPayment: item?.maxForPayment || 0,
						maxForConvert: item?.maxForConvert || 0,
					},
					newUserProps
				),
				companyId: item?.requestedByCompanyId,
				...contactStatus,
				...boNotes,
			},
		};
	}
	if (type === pendingRequestsTypes.BANK_ACCOUNTS && item) {
		props = {
			name: apiFunctionsEnum.APPROVE_BANK_ACCOUNT as string,
			data: {
				companyId: item.companyId,
				id: item.id,
				accountOwnerName: item.accountOwnerName,
				bankBranch: item.bankBranch,
				accountNumber: item.accountNumber,
				status: action === 'approve' ? 'Completed' : 'Rejected',
				...boNotes,
			},
		};
	}

	if (type === pendingRequestsTypes.GENERAL_SUPPORT && item) {
		props = {
			name: apiFunctionsEnum.APPROVE_SUPPORT_REQUEST as string,
			data: {
				requestId: id,
				summarize: BoNotes,
			},
		};
	}
	if ([pendingRequestsTypes.WITHDRAWAL_SUPPORT].includes(type as pendingRequestsTypes)) {
		props = {
			name: apiFunctionsEnum.APPROVE_SUPPORT_REQUEST as string,
			data: {
				requestId: id,
				summarize: _.isNil(BoNotes) ? item?.message : BoNotes,
				status: _.isEqual(action, 'reject') ? SupportStatus.Rejected : SupportStatus.Completed,
				companyBankAccountId: item?.companyBankAccountId,
				amount: item.amount,
				currencyId: item?.associatedCurrency?.id || item?.currency,
				contactId: item?.contactId,
				approveWithdraw: true,
			},
		};
		/*if (item) {
			props = { ...props, data: { ...item, ...props.data } };
		}*/
	}

	return props;
}

export function getCompanyFeatureProfileApprovedPayload(
	action: string,
	type: string,
	id: string,
	BoNotes: string,
	item?: Record<string, any>
) {
	let props: { name: string; data: Record<string, any> } = {
		name: '',
		data: {},
	};
	const boNotes = !_.isNil(BoNotes) ? { boNotes: BoNotes } : {};
	if (type === pendingRequestsTypes.BALANCES) {
		switch (item?.depositType) {
			case depositType.Digital:
				props = {
					name: apiFunctionsEnum.EDIT_DIGITAL_BALANCE_STATUS as string,
					data: {
						id,
						feezbackId: item?.iconductFeezbackId,
						boUserId: item?.boApprovedByUserId,
						status: item?.status,
					},
				};
				break;
			case depositType.Manual:
				props = {
					name: apiFunctionsEnum.BO_SIGNER_APPROVE_BALANCE as string,
					data: {
						id,
					},
				};
				break;
		}
	}
	if (type === pendingRequestsTypes.BENEFICIARIES) {
		props = {
			name: apiFunctionsEnum.APPROVE_BENEFICIARY as string,
			data: {
				beneficiaryId: id,
				status: action === 'approve' ? 'Approved' : 'Declined',
			},
		};
	}
	if (type === pendingRequestsTypes.PAYMENTS) {
		props = {
			name: apiFunctionsEnum.APPROVE_PAYMENT as string,
			data: {
				paymentId: id,
				status: action === 'approve' ? 'Approved' : 'Rejected',
				...boNotes,
			},
		};
	}
	if (type === pendingRequestsTypes.CONTACTS) {
		const contactStatus = action === 'approve' ? {} : { status: 'Rejected' };

		props = {
			name: apiFunctionsEnum.APPROVE_ACCOUNT as string,
			data: {
				contact: _.pick(
					{
						...item,
						role: item?.occupation,
						firstNameEnglish: item?.firstName,
						lastNameEnglish: item?.lastName,
						dateOfBirth: new Date(item?.year, item?.month, item?.day),
						phoneNumber: item?.phone,
						emailAddress: item?.email,
					},
					newUserProps
				),
				...contactStatus,
				...boNotes,
			},
		};
	}
	if (type === pendingRequestsTypes.BANK_ACCOUNTS && item) {
		props = {
			name: apiFunctionsEnum.APPROVE_BANK_ACCOUNT as string,
			data: {
				companyId: item.companyId,
				bankAccount: {
					id: item.id,
					accountOwnerName: item.accountOwnerName,
					bankBranch: item.bankBranch,
					accountNumber: item.accountNumber,
				},
				status: action === 'approve' ? 'Completed' : 'Rejected',
			},
		};
	}
	if (type === pendingRequestsTypes.GENERAL_SUPPORT && item) {
		props = {
			name: apiFunctionsEnum.APPROVE_SUPPORT_REQUEST as string,
			data: {
				requestId: id,
				summarize: BoNotes,
			},
		};
	}

	return props;
}

export function getCompanyFeatureProfileDeclinedPayload(
	action: string,
	type: string,
	id: string,
	BoNotes: string,
	item?: Record<string, any>
) {
	let props: { name: string; data: Record<string, any> } = {
		name: '',
		data: {},
	};
	if (type === pendingRequestsTypes.BALANCES) {
		props = {
			name: apiFunctionsEnum.BO_SIGNER_DECLINE_BALANCE as string,
			data: {
				id,
				reason: BoNotes,
			},
		};
	}

	return props;
}

export function getCompanyFeatureCreatePayload(type: string, item?: any) {
	const props: { name: string; data: Record<string, any> } = {
		name: '',
		data: {},
	};

	switch (type) {
		case pendingRequestsTypes.BALANCES:
			const {
				altshulerAccountId,
				amount,
				'bankAccount.id': bankAccountId,
				'approvedBy.id': contactId,
				file,
				houseAccountTransferReference: ccHouseAccountTransferReference,
				companyId,
				currencyId,
			} = item;
			props.name = apiFunctionsEnum.CREATE_BALANCE as string;
			props.data = {
				companyId,
				bankAccountId,
				altshulerAccountId,
				amount: Number(amount),
				contactId,
				file,
				currencyId,
			};
			if (ccHouseAccountTransferReference) {
				props.data = { ...props.data, ccHouseAccountTransferReference };
			}
			break;
		case pendingRequestsTypes.BANK_ACCOUNTS:
			props.name = apiFunctionsEnum.APPROVE_BANK_ACCOUNT;
			props.data = {
				companyId: item.companyId,
				file: item.file,
				accountOwnerName: item.accountOwnerName,
				bankId: item['bankName.bankCode'],
				bankBranch: item.bankBranch,
				accountNumber: item.accountNumber,
				status: 'Completed',
			};
			break;
		case pendingRequestsTypes.CONTACTS:
			props.name = apiFunctionsEnum.APPROVE_ACCOUNT;
			props.data = {
				companyId: item.companyId,
				contact: {
					..._.omit(item, ['day', 'month', 'year', 'companyId', 'email', 'firstName', 'lastName', 'phone']),
					dateOfBirth: new Date(item?.year, Number(item?.month), Number(item?.day) + 1),
					role: item.occupation,
				},
			};
			break;
		case pendingRequestsTypes.WITHDRAWAL_SUPPORT:
			(props.name = apiFunctionsEnum.CREATE_WITHDRAWAL_BALANCE as string),
				(props.data = {
					companyId: item.companyId,
					summarize: item.message,
					companyBankAccountId: item?.companyBankAccountId,
					amount: item.amount,
					currencyId: item?.currency,
					contactId: item?.approvedBy,
				});
	}

	return props;
}

export const getInitialApproveFormValues = (currentItem: any, type: string): any => {
	if (!currentItem) {
		return {};
	}
	const configuration: any = {
		[pendingRequestsTypes.BALANCES]: {
			// houseAccountTransferCheck: currentItem.ccHouseAccountTransferReference ? 'true' : 'false',
			houseAccountTransferReference: currentItem.ccHouseAccountTransferReference,
		},
	};
	return configuration[type];
};

export const getInitialProfileFormValues = (currentItem: any, type: string): any => {
	if (!currentItem) {
		return {};
	}
	const configuration: any = {
		[pendingRequestsTypes.BALANCES]: {
			selectApproveTitleNumber:
				currentItem?.boApprovedByMainSignerUserId || currentItem?.boApprovedBySecondarySignerUserId ? '2' : '1',
			'approvedBy.id': `${currentItem?.approvedBy?.firstName} ${currentItem?.approvedBy?.lastName}`,
			boApprovedByUserName: `${currentItem?.boApprovedByUser?.firstName} ${currentItem?.boApprovedByUser?.lastName}`,
			'currency.id': currentItem.currency.name,
			boSecondaryBoSignerApprovedByUserName: `${currentItem?.boApprovedByMainSignerUser?.firstName} ${currentItem?.boApprovedByMainSignerUser?.lastName}`,
			boFirstBoSignerApprovedByUserName: `${currentItem?.boApprovedBySecondarySignerUser?.firstName} ${currentItem?.boApprovedBySecondarySignerUser?.lastName}`,
			boADeclinedByUser: `${currentItem?.boADeclinedByUser?.firstName} ${currentItem?.boADeclinedByUser?.lastName}`,
			boFirstBoSignerApprovedByDate:
				currentItem.boApprovedByMainSignerUser || currentItem.boApprovedBySecondarySignerUser
					? currentItem.boApprovedByMainSignerUser
						? currentItem.boMainSignerApprovalDate
						: currentItem.boSecondarySignerApprovalDate
					: '',
			//houseAccountTransferCheck: currentItem.ccHouseAccountTransferReference ? 'true' : 'false',
			//message: currentItem?.boNotes,
			houseAccountTransferReference: currentItem.ccHouseAccountTransferReference || currentItem?.reference,
			bankName: `${_.get(currentItem, 'bankAccount.bankName.bankName')}  ${_.get(
				currentItem,
				'bankAccount.accountNumber'
			)}/${_.get(currentItem, 'bankAccount.bankBranch')}`,
		},
	};
	return configuration[type];
};

export const isFormNeedsHidingInProfile = (form: any, type: string, item: any): boolean => {
	const configuration: any = {
		[pendingRequestsTypes.BALANCES]: {
			'page.pendingRequests.balance.field.firstBoSigner': !item.boApprovedBySecondarySignerUser,
			'page.pendingRequests.balance.field.secondBoSigner': !item.boApprovedByMainSignerUser,
			'page.pendingRequests.balance.field.RejectedBoSigner': !item.boDeclinedByUserId,
			'page.pendingRequests.balance.field.bo.confirm.mainTitle':
				item.status !== BalanceStatus.PendingBoSignerReview,
		},
		[pendingRequestsTypes.BALANCES_BO_REQUESTS]: {
			'page.pendingRequests.balance.field.firstBoSigner': !item.boApprovedBySecondarySignerUser,
			'page.pendingRequests.balance.field.secondBoSigner': !item.boApprovedByMainSignerUser,
			'page.pendingRequests.balance.field.RejectedBoSigner': !item.boDeclinedByUserId,
			'page.pendingRequests.balance.field.bo.confirm.mainTitle': true,
		},
	};
	return configuration[type]?.[form.name] ?? false;
};

export const isFormNeedsHidingInApprove = (form: any, type: string, item: any): boolean => {
	const configuration: any = {
		[pendingRequestsTypes.BALANCES]: {
			'page.pendingRequests.balance.field.confirm.mainTitle': item.status !== BalanceStatus.Pending,
		},
	};
	return configuration[type]?.[form.name] ?? false;
};

export const getOptionsDependenciesByItemForApprove = (type: pendingRequestsTypes, item: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				'approvedBy.id': [
					{ label: `${item?.approvedBy.firstName} ${item?.approvedBy.lastName}`, value: item?.approvedBy.id },
				],
				'currency.id': [{ label: item?.currency.name, value: item?.currency.id }],
			};
		default:
			return {};
	}
};

export const getCustomDependenciesByItemForApprove = (type: pendingRequestsTypes, item: any, customData: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return {
				'bankAccount.id': {
					accounts: item.companyBankAccounts || [],
					selectedAccountId: item?.bankAccount?.id,
				},
				altshulerAccountId: {
					accounts: customData['altshulerAccountId'].accounts,
					selectedAccountId: item?.altshulerAccountId,
				},
			};
		default:
			return {};
	}
};

export const getOptionsDependenciesForProfile = (type: pendingRequestsTypes, item: any) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
		case pendingRequestsTypes.BALANCES_BO_REQUESTS:
			return {
				'approvedBy.id': [
					{
						label: `${item?.approvedBy?.firstName} ${item?.approvedBy?.lastName}`,
						value: item?.approvedBy?.id,
					},
				],
				'bankAccount.id': [
					{
						label: `${item?.approvedBy?.firstName} ${item?.approvedBy?.lastName}`,
						value: item?.approvedBy?.id,
					},
				],
				'currency.id': [{ label: item?.currency?.name, value: item?.currency?.id }],
			};
		default:
			return {};
	}
};

export const getCustomDependenciesForProfile = (
	type: pendingRequestsTypes,
	item: any,
	altshulerAccount: any = null
) => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
		case pendingRequestsTypes.BALANCES_BO_REQUESTS:
			return item.bankAccount
				? {
						'bankAccount.id': {
							accounts: [
								{
									accountNumber: item?.bankAccount?.accountNumber,
									bankName: item?.bankAccount?.bankName?.bankName,
									bankBranch: item?.bankAccount?.bankBranch,
									id: item?.bankAccount?.id,
								},
							],
							selectedAccountId: item?.bankAccount.id,
						},
						altshulerAccountId: {
							accounts: [
								{
									accountNumber: altshulerAccount.accountNumber,
									bankName: altshulerAccount.bank.bankName,
									bankBranch: altshulerAccount.bankBranch,
									id: altshulerAccount.id,
								},
							],
							selectedAccountId: altshulerAccount.id,
						},
				  }
				: {
						altshulerAccountId: {
							accounts: [
								{
									accountNumber: altshulerAccount.accountNumber,
									bankName: altshulerAccount.bank.bankName,
									bankBranch: altshulerAccount.bankBranch,
									id: altshulerAccount.id,
								},
							],
							selectedAccountId: altshulerAccount.id,
						},
				  };
		default:
			return {};
	}
};

export const isAllowSubmitForProfile = (type: pendingRequestsTypes, item: any): boolean => {
	switch (type) {
		case pendingRequestsTypes.BALANCES:
			return (
				item.status === BalanceStatus.PendingBoSignerReview ||
				(item.depositType === depositType.Digital && item.status === BalanceStatus.Pending)
			);
		default:
			return true;
	}
};

export const isAllowSubmitForApprove = (type: pendingRequestsTypes, item: any): boolean => {
	switch (type) {
		case pendingRequestsTypes.BENEFICIARIES:
			return item.status === generalStatusEnum.SIGNEDPENDINGBOAPPROVAL;
		case pendingRequestsTypes.BALANCES:
			return item.status === BalanceStatus.Pending;
		default:
			return true;
	}
};

export const getInitialFormValues = (type: string, item: any) => {
	switch (type) {
		case pendingRequestsTypes.CONTACTS:
			return {
				...item,
				firstName: item?.firstNameEnglish,
				lastName: item?.lastNameEnglish,
				phone: item?.phoneNumber,
				email: item?.emailAddress,
			};
		default:
			return item;
	}
};

export const applyDueDateStyling = (
	fields: any[],
	values: any,
	t: (key: string) => string,
	availableDates: string[] = []
) => {
	return fields.map((field) => {
		if (field.props?.name !== 'dueDate') return field;

		const date = startOfDay(new Date(values?.dueDate));
		const now = startOfDay(new Date());
		const nextAvailable =
			availableDates?.[0] && isValid(new Date(availableDates[0]))
				? format(new Date(availableDates[0]), 'dd/MM/yyyy')
				: '';

		if (isValid(date) && date < now) {
			return {
				...field,
				props: {
					...field.props,
					error: true,
					helperText: `${t('page.customerAccounts.forms.payment.details.dueDatePassed')} ${nextAvailable}`,
					sx: {
						...(field.props.sx || {}),
						border: '2px solid red',
						borderRadius: '6px',
						'& .MuiFormHelperText-root': {
							backgroundColor: 'transparent !important',
						},
					},
				},
			};
		}

		return field;
	});
};

// Helper function to add company balance validation for WITHDRAWAL_SUPPORT
export const addCompanyBalanceValidation = (
	baseValidation: any[],
	field: any,
	type: string | undefined,
	formState: Record<string, any>,
	formsToShow: any[],
	companyData: any
) => {
	// Only add validation for WITHDRAWAL_SUPPORT amount fields
	if (type === pendingRequestsTypes.WITHDRAWAL_SUPPORT && field.props.name === 'amount') {
		// Get current currency selection from form state
		const transferDetailsForm = _.find(
			formsToShow,
			(form) => form.name === 'page.pendingRequests.balance.field.transferDetails'
		);
		const transferDetailsFormName =
			transferDetailsForm?.name || 'page.pendingRequests.balance.field.transferDetails';
		const selectedCurrencyId = formState[transferDetailsFormName]?.values?.currency;

		if (selectedCurrencyId && companyData?.balances) {
			// Find company balance for selected currency
			const selectedBalance = companyData.balances.find(
				(balance: any) => balance.currencyId === Number(selectedCurrencyId)
			);

			if (selectedBalance) {
				// Check if exceedsBalance validation already exists
				let hasBalanceValidation = false;
				const updatedValidation = baseValidation.map((validation: any) => {
					if (_.has(validation, validationTypes.exceedsBalance)) {
						hasBalanceValidation = true;
						return {
							...validation,
							[validationTypes.exceedsBalance]: selectedBalance.amount,
						};
					}
					return validation;
				});

				// If no existing exceedsBalance validation, add it
				if (!hasBalanceValidation) {
					updatedValidation.push({
						[validationTypes.exceedsBalance]: selectedBalance.amount,
					});
				}

				return updatedValidation;
			}
		}
	}

	return baseValidation;
};

export const getFieldCustomizationsForProfile = (
	field: any,
	type: string,
	item: any,
	hasPermission?: boolean
): { mode?: string; disabled?: boolean } => {
	const customizations: any = {
		[pendingRequestsTypes.BALANCES]: {
			status: () => {
				const isPendingDigitalBalance =
					item?.depositType === depositType.Digital && item?.status === BalanceStatus.Pending;

				if (isPendingDigitalBalance && hasPermission) {
					return {
						mode: fieldMode.readWithEnableWrite,
						disabled: false,
					};
				}
				return {};
			},
		},
	};

	const fieldName = field.props.name;
	if (customizations[type] && customizations[type][fieldName]) {
		return customizations[type][fieldName]();
	}

	return {};
};
